<template>
  <div class="product-order">
    <form
      class="product-order-position"
      :class="{
        'product-order-position--editable': editable,
        'product-order-position__flyout': flyout
      }"
    >
      <div
        class="product-order-position__cell product-order-position__cell--image"
        :class="{'product-order-position__flyout__cell--image' : flyout}"
      >
        <a
          :href="url"
        >
          <img
            class="product-order-position__image"
            :src="image.src"
            :title="image.title"
            :alt="image.alt"
            width="80"
            height="80"
            loading="lazy"
          >
        </a>
      </div>
      <div
        class="product-order-position__cell"
        :class="[flyout ? 'product-order-position__flyout__cell--info' : 'product-order-position__cell--info']"
      >
        <div
          v-if="promotionTitle"
          class="product-order-position__promo"
        >
          {{ promotionTitle }}
        </div>
        <div :class="{ 'product-order-position__flyout--name-price' : flyout }">
          <a
            class="product-order-position__name"
            :class="{'product-order-position__flyout--name' : flyout}"
            :href="url"
          >
            {{ name }}
          </a>
          <div
            v-if="flyout && !isFreeItem"
            class="product-order-position__flyout--price"
            :class="{'product-order-position__flyout--price__promo' : rrpPrice}"
          >
            <span
              class="product-order-position__flyout--price__rrp"
              v-if="rrpPrice"
            >
              {{ $t('global.rrp') }} {{ rrpPrice }}
            </span>
            <span
              class="product-order-position__flyout--price"
              :class="{'product-order-position__flyout--price__total' : rrpPrice}"
            >
              {{ totalPrice }}
            </span>
          </div>
          <div
            v-if="flyout && isFreeItem"
            class="product-order-position__flyout--price product-order-position__flyout--price__free"
          >
            <span class="product-order-position__flyout--price__free-icon" />
            <span class="product-order-position__flyout--price__free-label">
              {{ $t('global.free.label') }}
            </span>
          </div>
        </div>
        <span
          class="product-order-position__product-nr"
          v-if="!flyout"
        >
          {{ $t('global.article.number') }}: {{ number }}
        </span>
        <a
          class="product-order-position__flyout--variations"
          :href="url"
          v-if="flyout"
        >
          <span
            v-if="variationMap.Farbe"
            class="product-order-position__flyout--variation"
          >
            {{ $t('global.color') }}: {{ variationMap.Farbe }}
          </span>
          <span
            v-if="variationMap.Größe"
            class="product-order-position__flyout--variation"
          >
            {{ $t('global.size') }}: {{ variationMap.Größe }}
          </span>
          <span
            v-if="type === 'minicart'"
            class="product-order-positio__flyout--variation"
          >
            {{ $t('global.mini.quantity') }}: {{ quantity }}
          </span>
        </a>
        <span
          v-if="flyout"
          class="product-order-position__flyout--action product-order-position__flyout--action__delete"
          @click.prevent="removeFromFlyout()"
          role="button"
          tabindex="0"
          @keydown="handleKeyDown"
          :aria-label="$t('global.article.remove.button')"
        >
          {{ $t('global.article.remove.button') }}
        </span>
        <div
          v-if="variations && variations.length && !flyout"
          class="product-order-position__variations"
          :class="{
            'product-order-position__variations--editable': isEditableNoPromo && !printable,
          }"
        >
          <div>
            <template v-if="!flyout">
              <span
                v-for="(variation, varIndex) in variations"
                :key="varIndex"
              >
                {{ variation }}
              </span>
            </template>
          </div>
          <div
            v-if="isEditableNoPromo && !printable"
            class="product-order-position__edit-button product-order-position__edit-button--desktop"
            @click.prevent="editProduct"
            role="button"
            tabindex="0"
            @keydown="handleKeyDown"
            :aria-label="$t('global.change.button')"
          >
            {{ $t('global.change.button') }}
          </div>
        </div>
        <div
          v-if="printable && !flyout"
          class="product-order-position__print-data"
        >
          {{ printDataString }}
        </div>
        <span
          v-if="!flyout && availability.color === 'BLUE'"
          class="product-order-position__availability"
          :class="availability.color"
        >
          {{ availability.text }}
        </span>
        <div
          v-if="deliveryInfo"
          class="product-order-position__delivery-info"
        >
          {{ deliveryInfo }}
        </div>
      </div>

      <div
        v-if="!flyout"
        class="product-order-position__cell product-order-position__cell--price"
      >
        <span
          v-if="!isFreeItem"
          class="product-order-position__price"
          :class="{
            'product-order-position__price--promo': rrpPrice
          }"
        >
          <span
            v-if="rrpPrice"
            class="product-order-position__price product-order-position__price--old"
          >
            <span v-if="rrpPrice">{{ $t('global.rrp') }}</span>
            <i>{{ rrpPrice }}</i>
          </span>
          <span
            v-if="discount && undiscountedQuantity === 0"
            class="product-order-position__price product-order-position__price--old"
          >
            <i>{{ originalPrice }}</i>
          </span>
          <i>{{ price }}</i>
        </span>
        <span
          v-else
          class="product-order-position__price--free"
        >
          <span class="product-order-position__price--free__icon" />
          <span class="product-order-position__price--promo">
            {{ $t('global.free.label') }}
          </span>
        </span>
      </div>
      <div
        v-if="type === 'orderTable'"
        class="product-order-position__cell product-order-position__cell--quantity"
      >
        <template
          v-if="isEditableNoPromo"
        >
          <cart-quantity-edit
            class="product-order-position__quantity-edit"
            :key="entryId"
            :initial="quantity"
            :position-id="entryId"
            :item-id="itemId"
            :graduated-prices="graduatedPricesDto.length > 1"
            :rounding-message="$t('counter.round.value')"
            :non-valid-input-message="$t('counter.nonvalid.value')"
            name="quantity"
            :min="minimumOrderQuantity"
            :max="maxPositionQuantity"
            :step="salesVolumeFactor"
          />
        </template>
        <template v-else>
          <span class="product-order-position__quantity-label">{{
            $t('global.quantity.label')
          }}: </span>

          <span>{{ quantity }}</span>
        </template>
      </div>

      <div
        class="product-order-position__cell product-order-position__cell--remove"
        v-if="!flyout"
        :class="{
          'product-order-position__cell--empty': !editable,
          'product-order-position__flyout__cell' : flyout
        }"
      >
        <template v-if="editable">
          <span
            class="product-order-position__edit-button"
            @click="editProduct"
            role="button"
            tabindex="0"
            @keydown="handleKeyDown"
            :aria-label="$t('global.change.button')"
          />
          <span
            class="product-order-position__remove"
            :aria-label="$t('remove.product.button.label')"
            @click="removeFromCart"
            role="button"
            tabindex="0"
            @keydown="handleKeyDown"
          />
        </template>
      </div>
      <div
        v-if="type === 'orderTable'"
        class="product-order-position__cell product-order-position__cell--summary"
      >
        <span class="product-order-position__price">
          {{ totalPrice }}
        </span>
      </div>
    </form>
  </div>
</template>

<script>
import CartQuantityEdit from '../cartQuantityEdit/cartQuantityEdit.vue'
import globalStateComputedMixin from '../../utilities/vueMixins/globalStateComputedMixin/globalStateComputedMixin.js'
import cartService from '../../services/cart/cart.js'
import dataLayerMixin from '../../utilities/vueMixins/dataLayerMixin/dataLayerMixin'

export default {
  mixins: [
    globalStateComputedMixin,
    dataLayerMixin
  ],
  components: {
    CartQuantityEdit
  },
  props: {
    url: String,
    image: Object,
    name: String,
    number: String,
    variations: {
      type: Array,
      default() {
        return []
      }
    },
    deliveryStatus: {
      type: Array,
      default() {
        return []
      }
    },
    graduatedPricesDto: {
      type: Array,
      default() {
        return []
      }
    },
    deliveryInfo: String,
    quantity: Number,
    undiscountedQuantity: Number,
    entryId: String,
    categoryId: String,
    price: String,
    originalPrice: String,
    rrpPrice: String,
    oldPrice: String,
    totalPrice: String,
    discount: String,
    discountTotal: String,
    promotionTitle: String,
    salesVolumeFactor: Number,
    minimumOrderQuantity: Number,
    purchasable: Boolean,
    availability: Object,
    isFreeItem: {
      type: Boolean,
      default: false
    },
    isSpecialPriceItem: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    selectedPromotionItemsCount: {
      type: Number,
      default: 0
    },
    maxSelectablePromotionItems: {
      type: Number,
      default: 0
    },
    editable: {
      type: Boolean,
      default: false
    },
    itemId: String,
    parentId: String,
    index: Number,
    variationMap: {
      type: Object,
      default: () => {
      }
    },
    type: String,
    flyout: Boolean,
    actionId: String,
    actionUrl: {
      type: String,
      default: ''
    },
    maxPositionQuantity: {
      type: Number,
      default: Number.MAX_VALUE
    },
    positionPrintData: {
      type: Object,
      default: null
    },
    printable: Boolean
  },
  computed: {
    positionType() {
      return 'cart'
    },
    isEditableNoPromo() {
      return this.editable && !this.isFreeItem && !this.isSpecialPriceItem
    },
    printDataString() {
      if (!this.positionPrintData) {
        return ''
      }

      let printData = this.positionPrintData
      let colors = [printData.cyan, printData.magenta, printData.yellow, printData.key]
      let values = []

      if (colors.every(value => typeof value === 'string' && value.trim() !== '')) {
        values.push([
          this.$t('checkout.basket.print.cyan', [printData.cyan]),
          this.$t('checkout.basket.print.magenta', [printData.magenta]),
          this.$t('checkout.basket.print.yellow', [printData.yellow]),
          this.$t('checkout.basket.print.key', [printData.key])
        ].join(''))
      }

      printData.printFiles.forEach(el => values.push(el.originalFileName))

      return values.join(' | ')
    }
  },
  methods: {
    prepareRequestData() {
      return {
        entryId: this.entryId,
        itemId: this.itemId,
        quantity: this.quantity,
        isFree: this.isFreeItem ? this.isFreeItem : '',
      }
    },
    editProduct() {
      this.$store.dispatch('editProduct', {
        type: this.positionType,
        requestData: this.prepareRequestData(),
        imageUrl: this.image,
        productName: this.name,
        productUrl: this.url
      })
      this.$store.dispatch('showModal', {id: this.positionType + 'Product', action: 'open'})
    },
    removeFromCart() {
      if (this.isFreeItem || this.isSpecialPriceItem) {
        cartService()
          .addPromoItem({
            url: this.actionUrl, data: {
              identifier: this.actionId,
              selected: false,
              itemId: this.itemId,
              _csrf: this.csrfToken.value
            }
          })
          .then(data => {
            if (data) {
              this.$store.dispatch('cartPage/updateCart', data)
            }
          })
      } else {
        this.$store.dispatch('cartPage/removeSingleProduct', {
          entryId: this.entryId,
          _csrf: this.csrfToken.value
        })
      }
    },
    removeFromFlyout() {
      this.$store.dispatch('reduceCount', {flyout: 'cart'})
      let data = {}
      data['itemId'] = this.itemId
      data['type'] = 'delete'
      this.removeFromCart()
      this.$store.dispatch('editArticleInFlyout', data)
    },
    handleKeyDown(event) {
      if (event.key === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        event.target.click();
      }
    }
  }
}

</script>
